import { IMonitorSalesProductItem } from 'entities/MonitorSales/MonitorSales.models';

// Each category has a fixed 5-stop palette, ordered from most saturated (highest sales share
// within the category) to lightest (lowest share). Keys are normalized (trimmed, lower-cased).
const CATEGORY_COLOR_PALETTES: Record<string, string[]> = {
  bigtime: ['#3795DE', '#61ABE4', '#8CC0E9', '#B6D6EF', '#E0EBF4'],
  sides: ['#8149F3', '#9B6EF6', '#B594F8', '#CEB9FB', '#E8DEFD'],
  bundles: ['#D25DE9', '#DB7EEE', '#E5A0F2', '#EEC1F7', '#F7E2FB'],
  beverage: ['#34D0C3', '#5EDACF', '#87E4DC', '#B1EDE8', '#DAF7F4'],
  premium: ['#E95DAF', '#EE7EC0', '#F2A0D0', '#F7C1E1', '#FBE2F1'],
  value: ['#FAD91B', '#FADF45', '#F9E56F', '#F9EA98', '#F8F0C2'],
  extras: ['#FF730A', '#FF913E', '#FFAF73', '#FFCDA7', '#FFEBDB'],
  others: ['#8491AE', '#97A2BB', '#C2C5D6', '#E0E3EB', '#EDEEF3'],
};

// Short codes used elsewhere in the app (e.g. category sales mock/labels) for the same categories.
const CATEGORY_ALIASES: Record<string, string> = {
  bt: 'bigtime',
};

const DEFAULT_CATEGORY = 'others';

function resolveCategoryKey(category: string): string {
  const normalized = category.trim().toLowerCase();
  const aliased = CATEGORY_ALIASES[normalized] ?? normalized;
  return CATEGORY_COLOR_PALETTES[aliased] ? aliased : DEFAULT_CATEGORY;
}

function getCategoryPalette(category: string): string[] {
  return CATEGORY_COLOR_PALETTES[resolveCategoryKey(category)];
}

// The category's base (most saturated) color — for legend dots, chart segments, etc.
export function getCategoryColor(category: string): string {
  return getCategoryPalette(category)[0];
}

function hexToRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function contrastTextColor(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1a1a2e' : '#ffffff';
}

export interface ICategoryShade {
  background: string;
  color: string;
}

// Picks a shade from the category's palette by rank within that category (0 = highest share).
// Higher share -> more saturated (earlier stop); lower share -> lighter (later stop).
export function pickCategoryShade(category: string, rank: number, count: number): ICategoryShade {
  const palette = getCategoryPalette(category);
  const ratio = count > 1 ? rank / (count - 1) : 0;
  const index = Math.round(ratio * (palette.length - 1));
  const background = palette[index];
  return { background, color: contrastTextColor(background) };
}

// Per-category descending rank within its category and category size, for products pre-sorted
// by salesPercent desc (so each category's subsequence stays sorted too).
export function buildCategoryRanks(products: IMonitorSalesProductItem[]): {
  rankInCategory: Map<IMonitorSalesProductItem, number>;
  countByCategory: Map<string, number>;
} {
  const rankInCategory = new Map<IMonitorSalesProductItem, number>();
  const countByCategory = new Map<string, number>();

  products.forEach((product) => {
    const rank = countByCategory.get(product.category) ?? 0;
    rankInCategory.set(product, rank);
    countByCategory.set(product.category, rank + 1);
  });

  return { rankInCategory, countByCategory };
}
